/**
 * ServiceFactory - Creates viewer services based on initialization options
 *
 * This is the core of the unified viewer architecture. It decides which
 * service implementations to use based on whether Firebase credentials
 * are provided.
 *
 * HOSTED MODE (firebaseConfig provided):
 * - FirebaseConfigProvider: Fetch config from Firestore
 * - FirebaseAnalyticsService: Real analytics tracking
 * - FirebaseUserService: User profile and feature access
 *
 * EXPORT MODE (no firebaseConfig, config object provided):
 * - EmbeddedConfigProvider: Use embedded config
 * - NoopAnalyticsService: No analytics (all methods are no-ops)
 * - StubUserService: All features enabled
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAnalytics, type Analytics } from 'firebase/analytics';
import { getFirestore, type Firestore } from 'firebase/firestore';

import type {
  InitOptions,
  ViewerServices,
  FirebaseConfig,
} from './interfaces';

// Firebase implementations
import { FirebaseConfigProvider } from './firebase/FirebaseConfigProvider';
import { FirebaseAnalyticsService } from './firebase/FirebaseAnalyticsService';
import { FirebaseUserService } from './firebase/FirebaseUserService';

// Stub implementations
import { EmbeddedConfigProvider } from './stub/EmbeddedConfigProvider';
import { NoopAnalyticsService } from './stub/NoopAnalyticsService';
import { StubUserService } from './stub/StubUserService';
import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('ServiceFactory');

/**
 * Initialized Firebase instances (cached to avoid re-initialization)
 */
let cachedFirebaseApp: FirebaseApp | null = null;
let cachedAnalytics: Analytics | null = null;
let cachedFirestore: Firestore | null = null;

/**
 * Initialize Firebase with the provided config
 * Returns cached instances if already initialized with same config
 */
function initializeFirebase(config: FirebaseConfig): {
  app: FirebaseApp;
  analytics: Analytics | null;
  db: Firestore;
} {
  // Check if we already have a cached app
  if (cachedFirebaseApp) {
    logger.debug('Using cached Firebase instances');
    return {
      app: cachedFirebaseApp,
      analytics: cachedAnalytics,
      db: cachedFirestore!,
    };
  }

  logger.info('Initializing Firebase...');

  // Initialize Firebase
  const app = initializeApp(config);
  const db = getFirestore(app);

  // Initialize Analytics (only in browser with measurementId)
  let analytics: Analytics | null = null;
  if (typeof window !== 'undefined' && config.measurementId) {
    try {
      analytics = getAnalytics(app);
      logger.info('Firebase Analytics initialized');
    } catch (error) {
      logger.warn('Analytics initialization failed:', error);
    }
  }

  // Cache the instances
  cachedFirebaseApp = app;
  cachedAnalytics = analytics;
  cachedFirestore = db;

  logger.info('Firebase initialized successfully');

  return { app, analytics, db };
}

/**
 * Create viewer services based on initialization options
 *
 * @param options Initialization options
 * @returns ViewerServices container with all service implementations
 */
export function createServices(options: InitOptions): ViewerServices {
  // HOSTED MODE: Firebase credentials provided
  if (options.firebaseConfig) {
    logger.info('Creating Firebase services (hosted mode)');

    const { app, analytics, db } = initializeFirebase(options.firebaseConfig);

    return {
      configProvider: new FirebaseConfigProvider(app, options.sequenceId),
      analyticsService: new FirebaseAnalyticsService(analytics, db),
      userService: new FirebaseUserService(app),
    };
  }

  // EXPORT MODE: No Firebase credentials, use embedded config
  if (options.config) {
    logger.info('Creating stub services (export mode)');

    return {
      configProvider: new EmbeddedConfigProvider(options.config),
      analyticsService: new NoopAnalyticsService(),
      userService: new StubUserService(),
    };
  }

  // Invalid options
  throw new Error(
    'Invalid initialization options: Must provide either firebaseConfig (hosted mode) or config (export mode)'
  );
}

/**
 * Detect the mode based on options
 */
export function getMode(options: InitOptions): 'hosted' | 'export' {
  return options.firebaseConfig ? 'hosted' : 'export';
}

/**
 * Re-export interfaces for convenience
 */
export type { InitOptions, ViewerServices, FirebaseConfig } from './interfaces';
